/**
 * General (Option C, C2): Name (was "Alias"; `alias` / `alias-input` kept, D4) and a labelled Color row. Both are shared
 * fields — they live in the form, never in an engine's draft.
 */
import { Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { CONNECTION_COLOR_PRESETS } from '../../components/connectionColorPresets';
import { FormRow } from '../pane/FormRow';
import { FormSection } from '../pane/FormSection';
import { NAME_PLACEHOLDERS } from './fieldLabels';
import type { ConnectionFormApi } from './fieldControls';

const SWATCH = "relative flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 before:absolute before:-inset-2 before:rounded-full before:content-['']";

export function GeneralSection({ form, engine }: { form: ConnectionFormApi; engine: DatabaseEngine }) {
  return (
    <FormSection id="general" title="General">
      <form.Field name="alias">
        {(field: any) => (
          <FormRow row="alias" label="Name" htmlFor="alias">
            <Input
              id="alias"
              name="alias"
              placeholder={NAME_PLACEHOLDERS[engine]}
              value={field.state.value ?? ''}
              onChange={(event) => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              data-testid="alias-input"
            />
          </FormRow>
        )}
      </form.Field>
      <form.Field name="color">
        {(field: any) => (
          <FormRow row="color" label="Color" groupId="connection-color-label">
            <div className="flex min-h-9 flex-wrap items-center gap-x-2.5 gap-y-2" data-testid="color-picker">
              <button
                type="button"
                className={cn(SWATCH, 'transition-colors', !field.state.value ? 'border-primary bg-muted' : 'border-transparent bg-muted/50 hover:bg-muted')}
                onClick={() => field.handleChange('')}
                title="No color"
                aria-label="No color"
                aria-pressed={!field.state.value}
                data-testid="color-none"
              >
                {!field.state.value && <span className="text-[8px] leading-none text-muted-foreground">—</span>}
              </button>
              {CONNECTION_COLOR_PRESETS.map(({ hex, label }) => (
                <button
                  key={hex}
                  type="button"
                  className={cn(SWATCH, 'transition-[border-color,scale]', field.state.value === hex ? 'scale-110 border-foreground' : 'border-transparent hover:scale-110')}
                  style={{ backgroundColor: hex }}
                  onClick={() => field.handleChange(hex)}
                  title={label}
                  aria-label={label}
                  aria-pressed={field.state.value === hex}
                  data-testid={`color-swatch-${label.toLowerCase()}`}
                >
                  {field.state.value === hex && <Check className="h-2 w-2 text-white drop-shadow-sm" />}
                </button>
              ))}
            </div>
          </FormRow>
        )}
      </form.Field>
    </FormSection>
  );
}
